import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, X } from "lucide-react";
import { videoStarted, videoStopped } from "../lib/mediaBus";

const VIDEO_ID = "x6uff6swo9o";
const PROMO_MP4 = "/images/anniv-promo.mp4";
const PROMO_WEBM = "/images/anniv-promo.webm";
// Promo shows after the intro through the end of Oct 19, 2026 (local time).
const PROMO_ACTIVE = () => new Date() < new Date(2026, 9, 20);

// Plays a muted background video ONCE per visit (session), then fades away
// to reveal the normal hero. Uses the YouTube IFrame API to detect "ended".
export default function HeroVideoBg() {
  const seen =
    typeof window !== "undefined" &&
    sessionStorage.getItem("uw_herovideo_seen") === "1";

  const [show, setShow] = useState(!seen);
  const [fade, setFade] = useState(false);
  const [promo, setPromo] = useState(false);
  const [muted, setMuted] = useState(true);
  const promoRef = useRef(null);
  const soundRef = useRef(false);

  const closeAll = () => {
    if (soundRef.current) {
      soundRef.current = false;
      videoStopped();
    }
    sessionStorage.setItem("uw_herovideo_seen", "1");
    setFade(true);
    setTimeout(() => setShow(false), 800);
  };

  const toggleSound = () => {
    const v = promoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    if (!next && !soundRef.current) {
      soundRef.current = true;
      videoStarted();
    } else if (next && soundRef.current) {
      soundRef.current = false;
      videoStopped();
    }
  };
  const holderRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!show || startedRef.current) return;
    startedRef.current = true;

    let player;
    let safety;
    let poll;
    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      clearInterval(poll);
      clearTimeout(safety);
      try {
        player && player.stopVideo && player.stopVideo();
      } catch (e) {}
      if (PROMO_ACTIVE()) setPromo(true);
      else closeAll();
    };

    const create = () => {
      if (!holderRef.current || !window.YT || !window.YT.Player) return;
      player = new window.YT.Player(holderRef.current, {
        videoId: VIDEO_ID,
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          playsinline: 1,
          rel: 0,
          loop: 0,
          modestbranding: 1,
          showinfo: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
        },
        events: {
          onReady: (e) => {
            try {
              e.target.mute();
              e.target.playVideo();
            } catch (err) {}
            // Poll playback position — dismiss once we reach the end, even if
            // the ENDED event never fires (prevents auto-replay looping).
            poll = setInterval(() => {
              try {
                const dur = player.getDuration ? player.getDuration() : 0;
                const cur = player.getCurrentTime ? player.getCurrentTime() : 0;
                if (dur > 0 && cur >= dur - 0.4) finish();
              } catch (err) {}
            }, 250);
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.ENDED) finish();
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      create();
    } else {
      if (!document.getElementById("yt-iframe-api")) {
        const s = document.createElement("script");
        s.id = "yt-iframe-api";
        s.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(s);
      }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prev === "function") prev();
        create();
      };
    }

    // Safety: if the API/"ended" event never fires, remove after 45s.
    safety = setTimeout(finish, 45000);

    return () => {
      clearInterval(poll);
      try {
        player && player.destroy();
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!show) return null;

  return (
    <div
      className={`hero-bg-wrap fixed inset-0 z-40 overflow-hidden pointer-events-none select-none bg-[#16110E] transition-opacity duration-700 ${
        fade ? "opacity-0" : "opacity-100"
      }`}
    >
      <div ref={holderRef} className="hero-bg-video" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#F3EAD7]/90" />
      {promo && (
        <div data-testid="promo-video-overlay" className="absolute inset-0 bg-[#16110E] flex items-center justify-center pointer-events-auto">
          <video
            ref={promoRef}
            data-testid="promo-video"
            className="w-full h-full object-contain"
            autoPlay
            muted
            playsInline
            onEnded={closeAll}
          >
            <source src={PROMO_MP4} type="video/mp4" />
            <source src={PROMO_WEBM} type="video/webm" onError={closeAll} />
          </video>
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-3">
            <button
              data-testid="promo-video-sound-btn"
              onClick={toggleSound}
              className="flex items-center gap-2 rounded-full bg-black/60 text-white px-4 py-2 text-sm backdrop-blur hover:bg-black/80 transition-colors"
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              {muted ? "Sound on" : "Mute"}
            </button>
            <button
              data-testid="promo-video-skip-btn"
              onClick={closeAll}
              className="flex items-center gap-2 rounded-full bg-[#C2541E] text-white px-4 py-2 text-sm hover:bg-[#a84617] transition-colors"
            >
              Skip <X size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
